/**
 * One control per field KIND - never per field name.
 *
 * This is the whole reason the back end was made generic. `status` and
 * `segment` are both enumerations and share a control; `parent` and `verifies`
 * are both references and share another. Adding a field to the profile costs a
 * line in services/schema.py and nothing here.
 *
 * The switch below is exhaustive over FieldKind, and two things in
 * FieldControl.test.tsx keep it that way: the kind list is DERIVED from the
 * FieldKind union (a `Record<FieldKind, …>`, so a new member fails `tsc -b`
 * until the list grows), and every kind in it is asserted NOT to reach the
 * escape hatch at the bottom of this file. The second half is the one that was
 * missing: the escape hatch renders a plausible-looking text box, so an
 * unhandled kind used to pass a test that only asked whether something
 * editable appeared. `quantity` was added to the union and nothing went red.
 *
 * A field carries its own unit (`FieldSchema.unit`), which is why nothing here
 * is keyed on a field NAME: the control for a mass is the control for a power
 * draw is the control for a fourth quantity nobody has declared yet.
 */

import { useState } from 'react'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Select from '@mui/material/Select'
import Stack from '@mui/material/Stack'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import FormControl from '@mui/material/FormControl'
import Button from '@mui/material/Button'
import ReferencePicker from './ReferencePicker'
import { splitQuantity } from './quantity'
// A bridge, not an API: components/parts/parts-editing.ts imports splitQuantity
// from here because it was defined here for an afternoon. The home is
// ./quantity - one parser for quantities, since two would disagree invisibly -
// and this line goes as soon as that import moves.
export { splitQuantity, quantityStated } from './quantity'
import { api } from '../../api'
import { forgetChoices, useChoices } from '../../hooks/useChoices'
// The same short form the budget tiles show: split on the last '::', so a unit
// from a package this code has never heard of still reads correctly.
import { sameUnit, unitLabel } from '../budgets/budget-model'
import { MONOSPACE } from '../../theme'
import { sortNaturally } from '../common/naturalCompare'
import type { FieldSchema, KindSchema, Schema } from '../../schema'

/**
 * The picker entry that means "add a value", not a value.
 *
 * A sentinel in the list rather than a button beside it, matching how the
 * wizard offers "New package…": the add option belongs to the list it grows,
 * and a new program's propellant list has nothing else in it.
 */
const ADD_OPTION = '::add-option'

export interface FieldControlProps {
  schema: Schema
  kind: KindSchema
  field: FieldSchema
  value: unknown
  onChange: (value: unknown) => void
  disabled?: boolean
  /** A complaint from the server about this field specifically. */
  error?: string
  /**
   * The accessible name, where the VISIBLE label lives outside this control.
   *
   * A table of attributes puts the label in its own column, so the control is
   * given `label: ''` to stop it being said twice - and an input with no label
   * has no accessible name at all, which is a control a screen reader announces
   * as "edit text" and a keyboard user cannot identify. This is that name.
   */
  ariaLabel?: string
  /**
   * The row being edited, for controls that WRITE something other than the
   * field's own value.
   *
   * Only "+ Add Option" needs it: adding to a vocabulary declares an enum, and
   * the enum needs a package to live in, which the part is what says. Absent
   * where the caller has no row - a create form editing nothing yet - and the
   * add option is then not offered rather than offered and broken.
   *
   * EXCEPT for a field whose schema says `can_add_choice`: its list is the
   * program's, so it is added to with no row named and this is not read.
   */
  target?: string
  /**
   * Suppress the unit adornment, where the unit is shown once for a whole group.
   *
   * DISPLAY ONLY. Blanking `field.unit` would do it visually and would also
   * change what gets WRITTEN: the control compares the stored value's unit
   * against the field's to decide whether a value is in an unexpected unit, so
   * an empty declaration makes every stored value look mismatched and be
   * re-composed as `-20 [SI::'°C_abs']` instead of the bare number the API
   * wants. The unit stays; only the adornment goes.
   */
  hideUnit?: boolean
  /** A `text` field that holds paragraphs: a multi-line box rather than one line. */
  multiline?: boolean
}

/** Read a value that should be a string, whatever arrived. */
function asText(value: unknown): string {
  if (value === null || value === undefined) return ''
  return String(value)
}

/** Read a value that should be a list of strings, whatever arrived. */
function asList(value: unknown): string[] {
  if (Array.isArray(value)) return value.map(String)
  if (typeof value === 'string' && value) return [value]
  return []
}


/**
 * Free text that offers what is already in use.
 *
 * `onInputChange`, not `onChange`: a freeSolo Autocomplete reports a typed value
 * through `onChange` only on Enter, so somebody who types a new category and
 * clicks Save would save nothing.
 */
function SuggestedTextControl({ kind, field, value, onChange, disabled, error }: FieldControlProps) {
  const { options } = useChoices(kind.name, field)
  return (
    <Autocomplete
      freeSolo
      disabled={disabled}
      options={options}
      inputValue={asText(value)}
      onInputChange={(_event, next) => onChange(next)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={field.label}
          error={Boolean(error)}
          helperText={error || field.help}
          size="small"
          fullWidth
        />
      )}
    />
  )
}

export default function FieldControl(props: FieldControlProps) {
  const { schema, kind, field, value, onChange, disabled, error } = props
  const help = error || field.help
  const common = {
    label: field.label,
    disabled,
    error: Boolean(error),
    helperText: help,
    fullWidth: true,
    size: 'small' as const,
  }

  switch (field.kind) {
    case 'doc':
      return (
        <TextField
          {...common}
          multiline
          minRows={3}
          value={asText(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      )

    case 'text':
      if (field.suggested) return <SuggestedTextControl {...props} />
      return (
        <TextField
          {...common}
          multiline={props.multiline}
          minRows={props.multiline ? 2 : undefined}
          value={asText(value)}
          onChange={(event) => onChange(event.target.value)}
        />
      )

    case 'name':
    case 'identifier':
      return (
        <TextField
          {...common}
          value={asText(value)}
          onChange={(event) => onChange(event.target.value)}
          slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
        />
      )

    case 'multiplicity':
      return (
        <TextField
          {...common}
          value={asText(value)}
          placeholder="[0..*]"
          onChange={(event) => onChange(event.target.value)}
          slotProps={{ input: { sx: { fontFamily: MONOSPACE } } }}
        />
      )

    case 'quantity':
      return <QuantityControl {...props} />

    case 'enum':
      return <EnumControl {...props} />

    case 'metadata_list':
      return <EnumControl {...props} multiple />

    case 'type':
      return (
        <Autocomplete
          freeSolo
          disabled={disabled}
          size="small"
          options={kind.types.map((choice) => choice.name)}
          value={asText(value)}
          onChange={(_event, next) => onChange(next ?? '')}
          onInputChange={(_event, next) => onChange(next)}
          getOptionLabel={(option) => option}
          renderOption={(optionProps, option) => {
            const choice = kind.types.find((entry) => entry.name === option)
            return (
              <Box component="li" {...optionProps} key={option}>
                <Box>
                  <Typography sx={{ fontFamily: MONOSPACE, fontSize: '0.85rem' }}>
                    {option}
                  </Typography>
                  {choice?.label && (
                    <Typography variant="caption" color="text.secondary">
                      {choice.label}
                    </Typography>
                  )}
                </Box>
              </Box>
            )
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              error={Boolean(error)}
              // freeSolo on purpose: the loader resolves a level through the
              // specialization chain, so a program-specific intermediate
              // type is legitimate and the menu must not be a constraint.
              helperText={help || 'Any valid type; the list is a shortcut, not a limit'}
            />
          )}
        />
      )

    case 'reference':
      return (
        <ReferencePicker
          schema={schema}
          target={field.target}
          label={field.label}
          helperText={help}
          error={Boolean(error)}
          disabled={disabled}
          value={asText(value) || null}
          onChange={(next) => onChange(next ?? '')}
        />
      )

    case 'reference_list':
      return (
        <ReferencePicker
          schema={schema}
          target={field.target}
          label={field.label}
          helperText={help}
          error={Boolean(error)}
          disabled={disabled}
          multiple
          value={asList(value)}
          onChange={(next) => onChange(next ?? [])}
        />
      )

    case 'text_list':
      return <TextListControl {...props} />

    case 'flag':
    case 'conjugate':
      return (
        <FormControl error={Boolean(error)}>
          <FormControlLabel
            control={
              <Switch
                checked={value === true}
                onChange={(event) => onChange(event.target.checked)}
                disabled={disabled}
              />
            }
            label={field.label}
          />
          {help && <FormHelperText>{help}</FormHelperText>}
        </FormControl>
      )
  }

  // Unreachable while FieldControl handles every kind. It exists so that a
  // registry which grows a kind renders something honest rather than nothing.
  return (
    <TextField
      {...common}
      value={asText(value)}
      onChange={(event) => onChange(event.target.value)}
      helperText={`${field.kind}: no control for this kind yet`}
    />
  )
}

/**
 * A number, with the field's own unit shown beside it.
 *
 * WHY THIS IS NOT A TEXT BOX. `text` writes a string literal, so editing a mass
 * through BEST wrote `:>> dryMass = "3.5 [SI::kg]";` where an `ISQ::MassValue`
 * belongs. Nothing caught it: a quoted string re-parses cleanly and BEST read
 * its own value back happily, so only the regenerated .sysml ever differed.
 *
 * WHAT IT SENDS. A bare number - `3.5`, as JSON, not `'3.5 [SI::kg]'`. The unit
 * belongs to the FIELD (`dryMass : ISQ::MassValue`) and the server composes the
 * quantity, so nobody types a bracket and nobody can mistype one. `''` clears
 * the binding, which is unset rather than zero.
 *
 * Mid-typing text is passed through verbatim instead of being coerced: `3.` and
 * `1e` are both on the way to a legal number, and normalising them through
 * `Number` on every keystroke would delete the character just typed. `-1.5`,
 * `.5` and `1e3` are all accepted by the server as written, so passing them
 * through is not a concession either.
 *
 * ONE EXCEPTION, deliberate. A model may state `dryMass = 250 [SI::g]` - the
 * server accepts a unit that is not the field's and the rollup reports the
 * mismatch by name. Editing the number of one and sending it bare would let the
 * server recompose it in kilograms, changing the model by a factor of a
 * thousand in silence. So a stated unit that differs is SHOWN and PRESERVED,
 * and only that case ever sends a composed quantity.
 */
function QuantityControl({
  field, value, onChange, disabled, error, ariaLabel, hideUnit,
}: FieldControlProps) {
  const stored = splitQuantity(value)
  // Keyed on the unit, never on the field's name: a fourth quantity added to
  // the profile arrives with its own unit and needs no change here.
  // COMPARED THE WAY THE SERVER COMPARES, on the last segment and without
  // quotes. A raw string comparison called `SI::kg⋅m²` and `SI::'kg⋅m²'`
  // different units - the loader stores what the model wrote and the profile
  // quotes any symbol that is not an identifier - so every inertia term
  // reported "stated in kg⋅m², not the kg⋅m² this field declares" and, worse,
  // was written back with its unit composed in rather than as a bare number.
  const stated = stored.unit && !sameUnit(stored.unit, field.unit) ? stored.unit : ''
  const shown = unitLabel(stored.unit || field.unit)
  const mismatch = stated
    ? `stated in ${unitLabel(stated)}, not the ${unitLabel(field.unit)} this field declares`
    : ''
  const help = error || mismatch || field.help

  const edit = (text: string) => {
    const trimmed = text.trim()
    // Empty CLEARS the binding. Not 0, and not '0': the rollup distinguishes a
    // part that states nothing from one that states nothing much.
    if (!trimmed) return onChange('')
    if (stated) return onChange(`${trimmed} [${stated}]`)
    const asNumber = Number(trimmed)
    const complete = Number.isFinite(asNumber) && String(asNumber) === trimmed
    return onChange(complete ? asNumber : trimmed)
  }

  return (
    <TextField
      label={field.label}
      value={stored.number}
      onChange={(event) => edit(event.target.value)}
      disabled={disabled}
      error={Boolean(error)}
      helperText={help}
      fullWidth
      size="small"
      // Says which state an empty box is in. A quantity nobody has stated is a
      // real answer, and it is not the same answer as zero.
      placeholder="(unset)"
      slotProps={{
        // inputMode rather than type="number": a number input silently empties
        // itself on '3.' and hands back a value the person did not type.
        htmlInput: { inputMode: 'decimal', ...(ariaLabel ? { 'aria-label': ariaLabel } : {}) },
        input: {
          sx: { fontFamily: MONOSPACE },
          endAdornment: shown && !hideUnit ? (
            <InputAdornment position="end">{shown}</InputAdornment>
          ) : undefined,
        },
      }}
    />
  )
}

/** A menu of enumeration members, discovered from the model. */
function EnumControl({
  kind,
  field,
  value,
  onChange,
  disabled,
  error,
  target,
  multiple = false,
}: FieldControlProps & { multiple?: boolean }) {
  const { options, loading, failed, retry } = useChoices(kind.name, field)
  const [adding, setAdding] = useState(false)
  const [draft, setDraft] = useState('')
  const [added, setAdded] = useState<string[]>([])
  const [refusal, setRefusal] = useState('')
  const help = refusal || error || field.help

  // Every value the picker offers: what the model declares, plus anything added
  // in this control since the page loaded. `useChoices` caches per field and the
  // cache is cleared on an add, but the hook does not refetch on its own - so
  // the value just added is carried here rather than vanishing until a reload.
  // Natural order, so 'sub2' comes before 'sub10' (issue #82); a bare sort() is text order.
  const offered = sortNaturally([...new Set([...options, ...added])])

  const stopAdding = () => {
    setAdding(false)
    setDraft('')
    setRefusal('')
  }

  // HOW a value gets added, decided once. Two routes exist and they differ in
  // what they need: a field the schema marks `can_add_choice` (a requirement's
  // budget) takes its value with NO entity named, so the create form can offer
  // it; every other add goes through the part the field is on, and without a
  // `target` there is no part to say where the enum lives. `null` is "no way to
  // add", which hides the option rather than offering one that would fail.
  const adder: ((value: string) => Promise<{ choices: string[] }>) | null =
    field.can_add_choice === true
      ? (next) => api.addSchemaChoice(kind.name, field.name, next)
      : target
        ? (next) => api.addChoice(target, field.name, next)
        : null

  const commit = async () => {
    const wanted = draft.trim()
    if (!wanted || !adder) return
    try {
      const answer = await adder(wanted)
      // Clear EVERY cached list, because one vocabulary can serve several
      // fields - a propellant added through `fuel` belongs to `oxidizer` too.
      forgetChoices()
      setAdded(answer.choices)
      onChange(wanted)
      stopAdding()
    } catch (problem) {
      // Shown in place rather than thrown: the refusals are things a person can
      // act on - the value is already there, or it is not a usable name.
      setRefusal(problem instanceof Error ? problem.message : String(problem))
    }
  }

  const current = multiple ? asList(value) : asText(value)

  // A vocabulary this field draws on, whether or not anything is IN it yet.
  // `enum_type` names a list outright; `base_type` names an abstract base each
  // program fills in - which is the propellant case, and the reason an empty
  // list is not the same as no list.
  const vocabulary = Boolean(field.enum_type || field.base_type)
  // Not over a list that could not be read (issue #91): it is not known to be
  // empty, and adding to it could declare a value it already has.
  const canAdd = vocabulary && adder !== null && !disabled && !failed

  // Nothing constrains it, so free text is the honest control.
  //
  // The usual reason is NOT a missing library, which is what this comment used
  // to say and which sends you looking in the wrong place. The library declares
  // the abstract BASE - `abstract attribute def PropellantKind` - and states
  // that each program supplies its own values:
  //
  //     enum def BalrogPropellant :> PropellantKind { propene; nitrousOxide; }
  //
  // An enum declared in a LIBRARY is deliberately skipped by the server as a
  // catalogue rather than a choice, so until a program writes that line there
  // are no members to offer and this is what a thruster's fuel looks like.
  // `subsystem` fills in because the programs did write theirs.
  // ONLY where the field names no vocabulary at all. An empty list on a field
  // that HAS one is a program that has not filled it in yet, and that is what
  // "+ Add Option" is for - falling back to free text there is what let
  // 'propene' and 'Nitrous' both be typed with nothing to reconcile them.
  // A list that FAILED to load is not an empty one, so it keeps the menu.
  if (!loading && !failed && options.length === 0 && !multiple && !canAdd) {
    return (
      <TextField
        label={field.label}
        value={asText(value)}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
        error={Boolean(error)}
        helperText={help}
        fullWidth
        size="small"
      />
    )
  }

  const labelId = `field-${kind.name}-${field.name}`
  return (
    <FormControl fullWidth size="small" error={Boolean(error) || failed} disabled={disabled || loading}>
      <InputLabel id={labelId}>{field.label}</InputLabel>
      {/* One Select serves both shapes; the generic keeps the handler typed
          rather than needing a cast at every use. */}
      <Select<string | string[]>
        labelId={labelId}
        label={field.label}
        multiple={multiple}
        value={current}
        onChange={(event) => {
          // The sentinel is a COMMAND, not a value. Letting it through would
          // write '::add-option' into the model as this part's fuel.
          const chosen = event.target.value
          if (chosen === ADD_OPTION || (Array.isArray(chosen) && chosen.includes(ADD_OPTION))) {
            return
          }
          onChange(chosen)
        }}
        renderValue={(chosen) =>
          Array.isArray(chosen) ? (
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
              {chosen.map((entry) => (
                <Chip key={entry} label={entry} size="small" />
              ))}
            </Stack>
          ) : (
            chosen
          )
        }
      >
        {!multiple && (
          <MenuItem value="">
            <em>(none)</em>
          </MenuItem>
        )}
        {offered.map((option) => (
          <MenuItem key={option} value={option}>
            {multiple && (
              <Checkbox checked={(current as string[]).includes(option)} size="small" />
            )}
            {option}
          </MenuItem>
        ))}
        {/* LAST, and always present where the field draws on a vocabulary. A
            new program's propellant list is empty, so this is the only entry
            it has - which is what makes an empty list actionable instead of a
            dead end. A sentinel value rather than a button, so it reads as
            part of the list the way "New package…" does on the wizard. */}
        {canAdd && (
          <MenuItem
            value={ADD_OPTION}
            onClick={() => {
              setAdding(true)
              setRefusal('')
            }}
          >
            <em>+ Add option…</em>
          </MenuItem>
        )}
      </Select>
      {failed ? (
        <FormHelperText component="div">
          Could not load the options.{' '}
          <Button size="small" onClick={retry} sx={{ minWidth: 0, p: 0, verticalAlign: 'baseline' }}>
            Retry
          </Button>
        </FormHelperText>
      ) : (
        help && <FormHelperText>{help}</FormHelperText>
      )}

      {/* The add row, shown only once "+ Add Option" has been chosen. A second
          deliberate step, which is the point: a value that joined the
          vocabulary because somebody mistyped one is indistinguishable
          afterwards from one they meant. */}
      {adding && (
        <Stack direction="row" spacing={1} sx={{ mt: 1, alignItems: 'flex-start' }}>
          <TextField
            size="small"
            autoFocus
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value)
              setRefusal('')
            }}
            placeholder={field.can_add_choice ? 'thermal' : 'propene'}
            label={`New ${field.label.toLowerCase()}`}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                void commit()
              }
              if (event.key === 'Escape') stopAdding()
            }}
          />
          <Button size="small" onClick={() => void commit()} disabled={!draft.trim()}>
            Add
          </Button>
          <Button size="small" color="inherit" onClick={stopAdding}>
            Cancel
          </Button>
        </Stack>
      )}
    </FormControl>
  )
}

/**
 * An ordered list of strings.
 *
 * Order and duplicates both matter - the profile declares notes 'ordered
 * nonunique' because they are chronological and two may legitimately read the
 * same - so this edits rows in place rather than offering a set.
 */
function TextListControl({ field, value, onChange, disabled, error }: FieldControlProps) {
  const items = asList(value)
  const help = error || field.help

  const replace = (index: number, text: string) => {
    const next = [...items]
    next[index] = text
    onChange(next)
  }

  return (
    <FormControl fullWidth error={Boolean(error)}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
        {field.label}
      </Typography>
      <Stack spacing={1}>
        {items.map((item, index) => (
          <Stack key={index} direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <TextField
              value={item}
              onChange={(event) => replace(index, event.target.value)}
              disabled={disabled}
              size="small"
              fullWidth
              multiline
              slotProps={{ htmlInput: { 'aria-label': `${field.label} ${index + 1}` } }}
            />
            <IconButton
              onClick={() => onChange(items.filter((_entry, at) => at !== index))}
              disabled={disabled}
              aria-label={`Remove ${field.label} ${index + 1}`}
              size="small"
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
        <Box>
          <Button size="small" onClick={() => onChange([...items, ''])} disabled={disabled}>
            Add {field.label.toLowerCase().replace(/s$/, '')}
          </Button>
        </Box>
      </Stack>
      {help && <FormHelperText>{help}</FormHelperText>}
    </FormControl>
  )
}
